import {
    EndpointRegistry,
    extractEndpointsToBeInvalidated,
    extractEndpointsToBeRemoved,
    HttpOptions,
    RegisteredHttpEndpoint,
} from '@luminovo/api-contracts';
import { getToken } from '@luminovo/auth';
import { isPresent } from '@luminovo/commons';
import { httpQueryKey, useDebugErrorHandler } from '@luminovo/data-stack';
import { http, uploadFileToBlobStorage } from '@luminovo/http-client';
import { useMutation, UseMutationOptions, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { z } from 'zod/v4';

import { HasPermissionForEndpointFn, useHasModulePermissions } from '@/shared/permissions/useModulePermissions';

type HttpOptionsWithFiles<T extends RegisteredHttpEndpoint> = HttpOptions<T> & { files: File[] | File };

export function useHttpFileUpload<T extends RegisteredHttpEndpoint>(
    endpoint: T,
    getUrl: (response: z.infer<EndpointRegistry[T]['responseBody']>) => string,
    options: {
        snackbarMessage: string | null;
        onSuccess?: UseMutationOptions<
            z.infer<EndpointRegistry[T]['responseBody']>,
            unknown,
            HttpOptionsWithFiles<T>,
            unknown
        >['onSuccess'];
        onError?: UseMutationOptions<
            z.infer<EndpointRegistry[T]['responseBody']>,
            unknown,
            HttpOptionsWithFiles<T>,
            unknown
        >['onError'];
        /**
         * If true, the shared default error handler is skipped for this mutation:
         * no generic error snackbar and no Sentry capture. Use for expected,
         * unactionable failures that the caller handles itself. The caller's
         * `onError` still runs.
         * @default false
         */
        suppressDefaultErrorHandler?: boolean;
        /** @default true */
        multiple?: boolean;
        maxFiles?: number;

        /**
         * Callback function to update the progress of the upload.
         * @param progress - The progress of the upload as a number between 0 and 1.
         */
        onProgress?: (progress: number | undefined) => void;

        /** Each retry also fetches a fresh upload URL, so it covers expired SAS links. Default: no retries. */
        retry?: UseMutationOptions<
            z.infer<EndpointRegistry[T]['responseBody']>,
            unknown,
            HttpOptionsWithFiles<T>,
            unknown
        >['retry'];
        retryDelay?: UseMutationOptions<
            z.infer<EndpointRegistry[T]['responseBody']>,
            unknown,
            HttpOptionsWithFiles<T>,
            unknown
        >['retryDelay'];
    },
): UseMutationResult<z.infer<EndpointRegistry[T]['responseBody']>, unknown, HttpOptionsWithFiles<T>, unknown> & {
    checkPermission: HasPermissionForEndpointFn<T>;
} {
    const {
        snackbarMessage,
        onSuccess = () => {},
        onError: userOnError,
        suppressDefaultErrorHandler = false,
        multiple = true,
        maxFiles = Infinity,
        retry,
        retryDelay,
    } = options;
    const snackbarKey = 'upload-progress';
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const defaultOnError = useDebugErrorHandler();
    const queryClient = useQueryClient();

    const { permissionForEndpoint } = useHasModulePermissions();
    const { checkPermission } = permissionForEndpoint(endpoint);

    const mutation = useMutation({
        mutationKey: httpQueryKey(endpoint),
        retry,
        retryDelay,
        mutationFn: async (variables: HttpOptionsWithFiles<T>) => {
            const files = toFileArray(variables.files);

            const progressByFile = new Map<File, { loadedBytes: number; totalBytes: number }>();

            const onProgress = ({
                file,
                loadedBytes,
                totalBytes,
            }: {
                file: File;
                loadedBytes: number;
                totalBytes: number;
            }) => {
                progressByFile.set(file, { loadedBytes, totalBytes });

                const progress =
                    Array.from(progressByFile.values()).reduce(
                        (acc, curr) => acc + curr.loadedBytes / curr.totalBytes,
                        0,
                    ) / progressByFile.size;

                if (progress > 0.99) {
                    // reset progress to undefined
                    options.onProgress?.(undefined);
                } else {
                    options.onProgress?.(progress);
                }
            };

            if (files.length > maxFiles) {
                throw new Error(`Too many files uploaded. Maximum number of files is ${maxFiles}`);
            }
            if (!multiple && files.length > 1) {
                throw new Error('Only one file can be uploaded at once');
            }

            const response = await http(endpoint, variables, getToken());

            await Promise.all(files.map((file) => uploadFileToBlobStorage(file, getUrl(response), { onProgress })));

            return response;
        },
        onSettled: () => {
            closeSnackbar(snackbarKey);
        },
        onSuccess: async (data, variables, onMutateResult, context) => {
            queryClient.removeQueries({ type: 'inactive' });

            await Promise.allSettled(
                extractEndpointsToBeRemoved(endpoint).map((endpoint) =>
                    queryClient.removeQueries({
                        queryKey: [endpoint],
                    }),
                ),
            );

            await Promise.allSettled(
                extractEndpointsToBeInvalidated(endpoint).map((endpoint) =>
                    queryClient.invalidateQueries({
                        queryKey: [endpoint],
                    }),
                ),
            );

            if (isPresent(snackbarMessage)) {
                enqueueSnackbar(snackbarMessage, {
                    variant: 'success',
                    anchorOrigin: { vertical: 'top', horizontal: 'center' },
                    autoHideDuration: 2000,
                });
            }

            await onSuccess(data, variables, onMutateResult, context);
        },
        onError: (error, variables, onMutateResult, context) => {
            userOnError?.(error, variables, onMutateResult, context);
            if (!suppressDefaultErrorHandler) {
                defaultOnError(error);
            }
        },
    });

    return { checkPermission, ...mutation };
}

function toFileArray(files: File | File[]): File[] {
    return Array.isArray(files) ? files : [files];
}

export interface BaseFile {
    url: string;
    name: string;
}

export interface PendingUploadFile extends BaseFile {
    file: File;
    state: 'PendingUpload';
}

export interface RemovedFile extends BaseFile {
    file: File;
    state: 'RemovedLocal';
}

export interface StoredInAzureFile extends BaseFile {
    file: null;
    state: 'StoredInAzure';
}

export interface PendingRemovalFile extends BaseFile {
    file: null;
    state: 'PendingRemoval';
}

export type AttachmentFile = PendingUploadFile | RemovedFile | StoredInAzureFile | PendingRemovalFile;

export function useAttachmentFileMutation<T>({
    mutateUpload,
    mutateDelete,
    mutateDuplicate,
    onSuccess,
    snackbarMessage,
}: {
    mutateUpload: (file: PendingUploadFile, context: T) => Promise<unknown>;
    mutateDelete?: (file: PendingRemovalFile, context: T) => Promise<unknown>;
    mutateDuplicate?: (file: StoredInAzureFile, context: T) => Promise<unknown>;
    onSuccess?: () => void;
    snackbarMessage: string | null;
}) {
    const { enqueueSnackbar } = useSnackbar();

    return useMutation({
        mutationFn: async ({ files, context }: { files: AttachmentFile[]; context: T }) => {
            const promises = files.map(async (file) => {
                switch (file.state) {
                    case 'PendingUpload':
                        return await mutateUpload(file, context);
                    case 'PendingRemoval':
                        return await mutateDelete?.(file, context);
                    case 'StoredInAzure':
                        return await mutateDuplicate?.(file, context);
                    case 'RemovedLocal':
                        return; /** no-op */
                }
            });

            await Promise.all(promises);
        },
        onSuccess: () => {
            if (snackbarMessage) {
                enqueueSnackbar(snackbarMessage, { variant: 'success' });
            }
            onSuccess?.();
        },
    });
}
